import { Component, EventEmitter, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Facebook, IPlatformContext, Platforms } from '@core/models';
import { PlatformContextState } from '@core/state/platform-context.state';
import { FacebookService } from '@core/services';
import { BehaviorSubject } from 'rxjs';
import { UserState } from '@core/state/user.state';
import { ConnectAdAccountService } from '@shared/components';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { BusinessState } from '@core/state/business.state';
import { tap } from 'rxjs/operators';

const NAVIGATE_TO_BUSINESS_STATE = {
  text: 'Please navigate to your business settings to connect your Instagram account.',
  buttonText: '',
  cancelButtonText: 'Navigate to Business Settings',
  title: 'Instagram Account Reconnection Required'
};
const VALIDATE_INSTAGRAM_ACCOUNT_STATE = {
  text: 'Below is your connected Instagram account information. Please review and confirm it to continue. If any details are incorrect, update them in your Business Settings.',
  buttonText: 'Re-connect Account Above',
  cancelButtonText: 'Edit in Business Settings',
  title: 'Instagram Account Validation Required'
};

@Component({
  selector: 'aayn-instagram-account-validate',
  templateUrl: './instagram-account-validate.component.html',
  providers: [ConnectAdAccountService]
})
export class InstagramAccountValidateComponent implements OnInit {
  loader$ = new BehaviorSubject<boolean>(false);
  selectedPage?: Facebook.IPage;
  text = NAVIGATE_TO_BUSINESS_STATE.text;
  buttonText = NAVIGATE_TO_BUSINESS_STATE.buttonText;
  cancelButtonText = NAVIGATE_TO_BUSINESS_STATE.cancelButtonText;
  title = NAVIGATE_TO_BUSINESS_STATE.title;
  confirmButton = new EventEmitter();
  cancelButton = new EventEmitter();
  protected platformContext: IPlatformContext = this.store.selectSnapshot(PlatformContextState.PlatformContextsMapped)
    .Meta;

  get metaPlatform() {
    return this.store.selectSnapshot(BusinessState.SelectedBusinessAdAccountByPlatform(Platforms.Meta));
  }
  isSubAccount = this.store.selectSnapshot(UserState.isInviterUser);
  selectedInstagramAccount?: Facebook.IInstagramAccount;

  constructor(
    private store: Store,
    private facebookService: FacebookService,
    private adAccountService: ConnectAdAccountService,
    public activeModal: NgbActiveModal,
    private router: Router
  ) {}

  ngOnInit() {
    if (this.isSubAccount) {
      this.title = 'Need Owner Action';
      this.text =
        'You need to confirm your connected instagram account to continue. Please ask the owner to re-connect instagram account again.';
      this.buttonText = '';
      this.cancelButtonText = 'Cancel';

      return;
    }
    this.initialize();
  }

  initialize() {
    if (!this.platformContext?.id) return;
    this.loader$.next(true);
    this.facebookService.getPages$(this.platformContext.id).subscribe(({ error, data }) => {
      if (error) {
        this.loader$.next(false);
        return;
      }
      const pageId = this.metaPlatform?.pageId;
      const page = data.pages.edges?.find((x) => x.node.id == pageId);
      this.selectedPage = page?.node;
      if (this.selectedPage) {
        this.getInstagramAccount(page!.node);
      } else {
        this.loader$.next(false);
      }
    });
  }

  getInstagramAccount(page: Facebook.IPage) {
    this.facebookService
      .getInstagramAccounts$({
        pageId: page.id
      })
      .subscribe({
        next: ({ error, data }) => {
          if (error) {
            this.loader$.next(false);
            return;
          }
          if (this.metaPlatform?.pageId === page.id) {
            this.selectedInstagramAccount = data.instagramAccounts?.find(
              (x) => x.legacyInstagramUserId === this.metaPlatform?.instagramAccountId
            );

            if (this.selectedInstagramAccount) {
              this.text = VALIDATE_INSTAGRAM_ACCOUNT_STATE.text;
              this.buttonText = VALIDATE_INSTAGRAM_ACCOUNT_STATE.buttonText;
              this.cancelButtonText = VALIDATE_INSTAGRAM_ACCOUNT_STATE.cancelButtonText;
              this.title = VALIDATE_INSTAGRAM_ACCOUNT_STATE.title;
            }
          }

          this.loader$.next(false);
        },
        error: () => {
          this.loader$.next(false);
        }
      });
  }

  confirm() {
    if (this.selectedInstagramAccount) {
      this.reConnectInstagramAccount()
        .pipe(
          tap(() => {
            this.activeModal.close(true);
            this.confirmButton.next();
          })
        )
        .subscribe();
      return;
    }
    this.activeModal.close();
  }

  cancel() {
    if (!this.isSubAccount) {
      const businessId = this.store.selectSnapshot(BusinessState.SelectedBusiness)!.id;
      this.router.navigate(['main', 'business', 'update', businessId]);
    } else {
      this.router.navigate(['main', 'create-ad']);
    }
    this.activeModal.dismiss();
    this.cancelButton.next();
  }

  reConnectInstagramAccount() {
    return this.adAccountService.connectAdAccount$(Platforms.Meta, {
      adAccountId: this.metaPlatform!.externalId,
      pageId: this.selectedPage!.id,
      instagramAccountId: this.selectedInstagramAccount!.id
    });
  }
}
